import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Icon } from '@/components/ui/icon';
import { chordShapes, chordsOf, displayKey, renderedContent, shapeKey } from '@/lib/song';
import { cn } from '@/lib/utils';
import { ChordCard } from './chord-card';
import { SongSheet } from './song-sheet';
import type { ViewerSong } from './song-viewer';

export type PrintOptions = { showChords: boolean; showDiagrams: boolean };

/** Uma cifra para impressão: fundo branco, modo Nítido sem cores, 2 colunas, diagramas opcionais no fim. */
export const PrintSong: React.FC<
  {
    song: ViewerSong;
    /** Posição no repertório: cabeçalho "nº · título · tom". */
    number?: number;
  } & Partial<PrintOptions>
> = ({ song, number, showChords = true, showDiagrams = false }) => {
  const input = {
    originalKey: song.originalKey,
    setlistKey: song.key,
    capo: song.capo,
  };
  const content = renderedContent(song.content, input);
  const key = displayKey(input);
  const chords = chordsOf(content);

  return (
    <article
      className={cn(
        // na tela, simula a folha A4 inteira (210×297mm, 12mm de margem); ao imprimir a margem vem do @page
        // e a altura é a da própria página
        'mx-auto flex h-[297mm] w-full max-w-[210mm] flex-col break-after-page bg-white p-[12mm] text-black',
        'last:break-after-auto print:block print:h-auto print:max-w-none print:p-0',
        'rounded-[14px] print:rounded-none',
      )}
    >
      <header className="mb-5 border-black/20 border-b pb-3">
        <h1 className="font-bold font-display text-[26px] leading-tight">
          {number !== undefined && `${number} · `}
          {song.title}
          <span className="ml-3 font-semibold text-[18px]">· {key}</span>
        </h1>
        <p className="mt-1 text-[13px] text-black/70">
          {[
            song.artist,
            song.capo
              ? `Capo na ${song.capo}ª casa · formas de ${shapeKey(input)}`
              : null,
            song.notes,
          ]
            .filter(Boolean)
            .join(' · ')}
        </p>
      </header>

      {/* Na tela a altura da folha só vale para encher as colunas (1ª até o pé, depois a 2ª) se for
          definida: flex aninhado não basta, por isso a cifra fica absoluta dentro do espaço restante. */}
      <div className="relative min-h-0 flex-1 print:static">
        <SongSheet
          plain
          columns
          showChords={showChords}
          content={content}
          fontSize={15}
          className="absolute inset-0 overflow-hidden print:static print:overflow-visible"
        />
      </div>

      {showDiagrams && chords.length > 0 && (
        <div className="mt-6 flex flex-wrap gap-2 break-inside-avoid border-black/20 border-t pt-4">
          {chords.map((chord) => (
            <ChordCard
              key={chord}
              name={chord}
              shape={chordShapes[chord]}
              onLight
              className="w-[76px]"
            />
          ))}
        </div>
      )}
    </article>
  );
};

/** Barra da tela de impressão (some ao imprimir): escolhe o que sai na folha antes de imprimir. */
export const PrintShell: React.FC<{
  onBack: () => void;
  children: (options: PrintOptions) => React.ReactNode;
}> = ({ onBack, children }) => {
  const [showChords, setShowChords] = useState(true);
  const [showDiagrams, setShowDiagrams] = useState(false);

  return (
    <div className="print-root flex flex-col gap-5 print:block">
      {/* ancestrais flex ao imprimir geram páginas em branco no fim das colunas: vira tudo bloco */}
      <style>
        {
          '@page { size: A4; margin: 12mm; } @media print { *:has(.print-root) { display: block !important; } }'
        }
      </style>
      <div className="flex flex-wrap items-center gap-3 print:hidden">
        <Button type="button" variant="outline" onClick={onBack}>
          <Icon name="chevron-left" />
          Voltar
        </Button>
        <Button
          type="button"
          variant={showChords ? 'sand' : 'outline'}
          aria-pressed={showChords}
          onClick={() => setShowChords((value) => !value)}
        >
          <Icon name="music-4" />
          Mostrar cifra
        </Button>
        <Button
          type="button"
          variant={showDiagrams ? 'sand' : 'outline'}
          aria-pressed={showDiagrams}
          onClick={() => setShowDiagrams((value) => !value)}
        >
          <Icon name="layout-grid" />
          Mostrar diagramas
        </Button>
        <Button type="button" variant="success" onClick={() => window.print()}>
          <Icon name="printer" />
          Imprimir
        </Button>
      </div>
      {children({ showChords, showDiagrams })}
    </div>
  );
};
